/**
 * Slide list items from where they were to where they now are (FLIP).
 *
 * The Today board re-sorts itself under the tech: starting a timer lifts that
 * work order to the top and drops the one it displaced. React re-renders the
 * new order in a single frame, so a card teleports — the eye gets no chance
 * to follow which card went where, and on a board of similar-looking cards
 * that is the difference between "my job moved up" and "the list changed".
 *
 * FLIP: having rendered the new layout, compare each card's position with
 * where it sat on the previous commit, transform it *back* to the old spot
 * with no transition, then release it on the next frame so it travels to its
 * real position under one. The DOM is only ever laid out once — the movement
 * is a compositor-only transform, so a re-sorting list stays cheap.
 *
 * Positions are measured document-relative (`rect.top + scrollY`) rather than
 * viewport-relative, so scrolling between two commits is not mistaken for
 * cards having moved.
 *
 * Cards that change section unmount and remount under a different parent, so
 * the map is keyed by work-order id rather than by node — a remounted card is
 * still the same card, and still slides from where its predecessor stood.
 *
 * A node this hook moves has its CSS animation suppressed for the trip: an
 * entrance keyframe animating `transform` would otherwise beat the inline
 * transform (animations win over inline styles) and the slide would not
 * happen at all. Cards with no previous position — genuinely new to the page
 * — are left alone, so their entrance animation still plays.
 */
import { useCallback, useLayoutEffect, useRef } from 'react'

/** Matches `--transition-base`; the ease matches `--ease-out`. */
const MOVE_MS = 220
const MOVE_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'
/** Sub-pixel drift from re-layout is not movement worth animating. */
const MIN_TRAVEL_PX = 2

export interface FlipMove {
  /** `ref={flip.register(id)}` on each item's outermost element. */
  readonly register: (id: string) => (node: HTMLDivElement | null) => void
}

export function useFlipMove(): FlipMove {
  const nodes = useRef(new Map<string, HTMLDivElement>())
  const positions = useRef(new Map<string, number>())

  const register = useCallback(
    (id: string) => (node: HTMLDivElement | null) => {
      if (node) nodes.current.set(id, node)
      else nodes.current.delete(id)
    },
    [],
  )

  // No dep array: the list can re-sort on any render — a refetch, a mutation,
  // a tick — and every one of those needs its positions re-read.
  useLayoutEffect(() => {
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const next = new Map<string, number>()
    for (const [id, node] of nodes.current) {
      next.set(id, node.getBoundingClientRect().top + window.scrollY)
    }

    if (!reduced) {
      for (const [id, top] of next) {
        const before = positions.current.get(id)
        if (before === undefined) continue
        const travel = before - top
        if (Math.abs(travel) < MIN_TRAVEL_PX) continue

        const node = nodes.current.get(id)
        if (!node) continue
        const animation = node.style.animation
        node.style.animation = 'none'
        node.style.transition = 'none'
        node.style.transform = `translateY(${travel}px)`

        requestAnimationFrame(() => {
          node.style.transition = `transform ${MOVE_MS}ms ${MOVE_EASE}`
          node.style.transform = ''
          const done = () => {
            node.style.transition = ''
            node.style.animation = animation
            node.removeEventListener('transitionend', done)
          }
          node.addEventListener('transitionend', done)
        })
      }
    }

    positions.current = next
  })

  return { register }
}
